import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useGameStore } from './game.store';
import { authService } from '@/services/auth.service';
import type { SessionEnd } from '@/services/sessionPolicy';

/** Suffixe d'une permission de grade en lecture seule (miroir de PanelPermissions.READ_SUFFIX). */
export const READ_SUFFIX = ':read';

/**
 * Pages déplacées : une ancienne clé de page ouvre encore la nouvelle, en LECTURE seulement (miroir
 * de PanelPermissions.LEGACY_EQUIVALENTS). `config.grades` (ancienne page Grades de Configuration)
 * ouvre ainsi les Grades in-game de Supervision en lecture ; écrire dans LuckPerms exige
 * `<projet>.grades`, accordé exprès (avant, seul un admin global le pouvait).
 */
const LEGACY_PAGE_EQUIVALENTS: Record<string, string[]> = {
  grades: ['config.grades'],
};

/** Clés qui ouvrent `pageKey` en lecture : elle-même, puis ses anciennes clés équivalentes. */
function pagesGranting(pageKey: string): string[] {
  return [pageKey, ...(LEGACY_PAGE_EQUIVALENTS[pageKey] ?? [])];
}

export interface StaffUser {
  id: string;
  gamertag: string;        // handle Recube (= pseudo)
  xuid: string;            // sub Recube
  role: StaffRole;
  displayRole?: string;    // nom du/des grade(s) panel (ou "Administrateur")
  permissions: Permission[];
  avatarUrl?: string;
  isAdmin?: boolean;       // admin panel (bootstrap ou grade admin) → accès total
  gradeIds?: string[];     // grades attribués (un user peut en cumuler plusieurs)
  /** Set complet des permissions <projet>.<page> (tous projets). Le scoping par jeu actif se fait dans hasPage. */
  panelPermissions?: string[];
  manageAccess?: boolean;       // peut ouvrir « Grades & accès »
  canAssignGrades?: boolean;    // peut attribuer des grades
  assignableGradeIds?: string[]; // grades qu'il a le droit d'attribuer
  /** Projets dont le user est ADMIN (toutes les pages de ces projets, sans gestion des grades). */
  adminProjects?: string[];
  /**
   * Accès d'urgence : la base des grades du panel est injoignable et ce compte figure dans
   * PANEL_EMERGENCY_ADMINS, il a donc des droits admin TEMPORAIRES (bandeau rouge dans le layout).
   * Relu depuis /api/auth/me tant qu'il est posé : il tombe seul au retour de la base.
   */
  emergency?: boolean;
  /**
   * Groupes de jeu permis par projet à groupes (NationsGlory : Nova, Iris...), vus par l'API :
   * clés permises, ou null = tous (aucune affectation, admin global ou du projet). Projet absent =
   * tous ses groupes (sessions ouvertes avant l'affectation). Le serveur reste juge (passerelle).
   */
  gameGroups?: Record<string, string[] | null>;
}

export enum StaffRole {
  ADMIN = 'admin',
  MODERATOR = 'moderator',
  HELPER = 'helper',
  BUILDER = 'builder',
}

export enum Permission {
  // Dashboard
  VIEW_DASHBOARD = 'view_dashboard',
  VIEW_STATS = 'view_stats',

  // Players
  VIEW_PLAYERS = 'view_players',
  SEARCH_PLAYERS = 'search_players',

  // Sanctions
  VIEW_SANCTIONS = 'view_sanctions',
  CREATE_SANCTION = 'create_sanction',
  EDIT_SANCTION = 'edit_sanction',
  DELETE_SANCTION = 'delete_sanction',

  // Console
  VIEW_CONSOLE = 'view_console',
  SEND_COMMAND = 'send_command',

  // Logs
  VIEW_LOGS = 'view_logs',

  // Settings
  VIEW_SETTINGS = 'view_settings',
  EDIT_SETTINGS = 'edit_settings',
}

interface AuthState {
  user: StaffUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  /**
   * Le serveur a refusé la session (401) alors que le token semble encore valide côté client :
   * signature rejetée, horloges décalées, secret changé... On l'expose pour afficher une bannière
   * de reconnexion au lieu de laisser des pages vides sans explication.
   */
  sessionRejected: boolean;
  /**
   * Session finie (jeton échu, refusé, ou durée maximale atteinte) pendant que la page est ouverte :
   * une fenêtre propose de se reconnecter dans un nouvel onglet, sans rien jeter de la page. Non
   * conservé au rechargement : la première requête le redit si besoin.
   */
  sessionEnd: SessionEnd | null;

  // Actions
  setAuth: (user: StaffUser, accessToken: string, refreshToken: string) => void;
  /** Remplace le user de la session en cours (relecture de /api/auth/me), sans toucher au jeton. */
  setUser: (user: StaffUser) => void;
  updateUserRole: (role: StaffRole, displayRole: string) => void;
  logout: () => void;
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  hasAllPermissions: (permissions: Permission[]) => boolean;
  hasPage: (pageKey: string) => boolean;
  /** Droit d'ÉCRIRE sur cette page du projet actif (une perm en lecture seule ne suffit pas). */
  canWrite: (pageKey: string) => boolean;
  hasProject: (projectId: string) => boolean;
  markSessionRejected: () => void;
  clearSessionRejected: () => void;
  markSessionEnded: (end: SessionEnd) => void;
  clearSessionEnded: () => void;
  /** Jeton renouvelé pendant la session : seul le jeton change, le user reste. */
  setAccessToken: (accessToken: string) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      sessionRejected: false,
      sessionEnd: null,

      setAuth: (user, accessToken, refreshToken) => {
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          sessionRejected: false,
          sessionEnd: null,
        });
      },

      setUser: (user) => set({ user }),

      markSessionRejected: () => set({ sessionRejected: true }),
      clearSessionRejected: () => set({ sessionRejected: false }),
      markSessionEnded: (end) => set({ sessionEnd: end }),
      clearSessionEnded: () => set({ sessionEnd: null }),
      setAccessToken: (accessToken) => set({ accessToken }),

      updateUserRole: (role, displayRole) => {
        const { user } = get();
        if (user) {
          set({
            user: { ...user, role, displayRole },
          });
        }
      },

      logout: () => {
        // Le jeton du navigateur part avec la session : sinon un autre onglet resté ouvert pourrait
        // encore s'en servir, et le renouveler (cf session.ts).
        authService.clearToken();
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          sessionRejected: false,
          sessionEnd: null,
        });
      },

      hasPermission: (permission) => {
        const { user } = get();
        if (!user) return false;
        if (user.role === StaffRole.ADMIN) return true;
        return user.permissions.includes(permission);
      },

      hasAnyPermission: (permissions) => {
        const { hasPermission } = get();
        return permissions.some((p) => hasPermission(p));
      },

      hasAllPermissions: (permissions) => {
        const { hasPermission } = get();
        return permissions.every((p) => hasPermission(p));
      },

      hasPage: (pageKey) => {
        const { user } = get();
        if (!user) return false;
        if (user.isAdmin || user.role === StaffRole.ADMIN) return true;
        // Scoping par PROJET : une page n'est accordée que si le grade la possède POUR LE JEU ACTIF.
        // (Évite qu'une perm paladium.sanctions débloque la page Sanctions sous NationsGlory.)
        // Le serveur étend déjà panelPermissions pour un admin de projet ; le test adminProjects
        // ci-dessous couvre les sessions ouvertes AVANT ce déploiement (store persisté).
        const project = useGameStore.getState().activeGameId;
        if ((user.adminProjects ?? []).includes(project)) return true;
        // Une permission vaut `<projet>.<page>` (écriture) ou `<projet>.<page>:read` (lecture
        // seule) : les deux ouvrent la page, seule la première autorise à modifier (cf. canWrite).
        const perms = user.panelPermissions ?? [];
        return pagesGranting(pageKey).some((key) =>
          perms.includes(`${project}.${key}`) || perms.includes(`${project}.${key}${READ_SUFFIX}`));
      },

      canWrite: (pageKey) => {
        const { user } = get();
        if (!user) return false;
        if (user.isAdmin || user.role === StaffRole.ADMIN) return true;
        const project = useGameStore.getState().activeGameId;
        if ((user.adminProjects ?? []).includes(project)) return true;
        // Écriture : la clé de la page elle-même, jamais une ancienne clé équivalente.
        return (user.panelPermissions ?? []).includes(`${project}.${pageKey}`);
      },

      // Un projet n'est visible que si le user y a AU MOINS une permission (admin = tous).
      hasProject: (projectId) => {
        const { user } = get();
        if (!user) return false;
        if (user.isAdmin || user.role === StaffRole.ADMIN) return true;
        if ((user.adminProjects ?? []).includes(projectId)) return true;
        const prefix = `${projectId}.`;
        return (user.panelPermissions ?? []).some((p) => p.startsWith(prefix));
      },
    }),
    {
      name: 'recubebe-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
